import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useUpdateProfile, useDistrictList } from '../api'
import { ACCEPTED_FILE_TYPES, AdminSchema, formSchema } from './schema'
import FormBuilder from '../../../../components/app/formBuilder'
import { Button } from '../../../../components/common'
import { ROLE_DISPLAY_NAME } from '../../../Login/api'
import { useAuthStore } from '../../../../store/authStore'
import React from 'react'
import { useFetchUserDetails } from '../../api'
interface FormField {
  name: string
  label: string
  type: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  desc?: string
  descId?: string
  id?: string
  data?: { id: string | number; name: string }[]
  roleId?: number
}

interface ProfileCreateProps {
  isEditing: boolean
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
}

const formsByRole: Record<number, any[]> = {
  2: [
    { name: 'dob', label: 'Date Of Birth', type: 'date', required: true },
    {
      name: 'gender',
      label: 'Gender',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'Male', name: 'Male' },
        { id: 'Female', name: 'Female' },
        { id: 'Other', name: 'Other' },
      ],
    },
    {
      name: 'religion',
      label: 'Religion',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'hindu', name: 'Hindu' },
        { id: 'muslim', name: 'Muslim' },
        { id: 'christian', name: 'Christian' },
        { id: 'other', name: 'Other' },
      ],
    },
    {
      name: 'religion_other',
      label: 'Specify Religion',
      type: 'text',
      required: false,
      dependsOn: { field: 'religion', value: 'other' },
    },
    {
      name: 'caste',
      label: 'Caste',
      type: 'radio',
      required: true,
      data: [
        { id: 1, value: 'sc_st', radioLabel: 'SC/ST' },
        { id: 2, value: 'obc', radioLabel: 'OBC' },
        { id: 3, value: 'general', radioLabel: 'General' },
      ],
    },
    {
      name: 'qualification',
      label: 'Highest Qualification',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'qualification',
      data: [
        { id: 'Less than 10th', name: 'Less than 10th' },
        { id: '10th', name: '10th' },
        { id: '12th', name: '12th' },
        { id: 'Diploma', name: 'Diploma' },
        { id: 'UG', name: 'UG' },
        { id: 'PG', name: 'PG' },
        { id: 'PhD', name: 'PhD' },
        { id: 'Post Doc', name: 'Post Doc' },
      ],
    },
    {
      name: 'district',
      id: 'district_id',
      label: 'District',
      type: 'custom_select',
      placeholder: 'Select District',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [],
    },
    {
      name: 'business_idea',
      label: 'Business Idea',
      type: 'textarea',
      required: true,
    },
    {
      name: 'investment_capability',
      label: 'Investment Capability of the Applicant (in Lakhs)',
      type: 'number',
      required: true,
    },
    {
      name: 'attended_kied_programme',
      label: 'Ever Attended a KIED Programme?',
      type: 'radio',
      required: true,
      data: [
        { id: 6, value: 'yes', radioLabel: 'Yes' },
        { id: 7, value: 'no', radioLabel: 'No' },
      ],
    },
    {
      name: 'program_details_attended',
      label: 'Specify the Program Name',
      type: 'text',
      required: true,
      dependsOn: { field: 'attended_kied_programme', value: 'yes' },
    },
    {
      name: 'how_you_know_kied',
      label: 'How Did You Come to Know About KIED',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'social_media', name: 'Social Media' },
        { id: 'friends', name: 'Friends' },
        { id: 'media', name: 'Print Media' },
        { id: 'other', name: 'Others' },
      ],
    },
  ],
  3: [
    {
      name: 'school_name',
      label: 'School/College Name',
      type: 'text',
      required: true,
    },
    { name: 'address', label: 'Address', type: 'textarea', required: true },
    {
      name: 'head_name',
      label: 'Name of Head of the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'head_designation',
      label: 'Designation of Head of the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'head_email',
      label: 'E-mail of Head of the Institution',
      type: 'email',
      required: true,
    },
    {
      name: 'head_contact_no',
      label: 'Contact Number of Head of the Institution',
      type: 'number',
      required: true,
    },
    {
      name: 'name_spc',
      label: 'Name of Single Point of Contact at the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'designation_spc',
      label: 'Designation of Single Point of Contact',
      type: 'text',
      required: true,
    },
    {
      name: 'email_spc',
      label: 'E-mail of Single Point of Contact',
      type: 'email',
      required: true,
    },
    {
      name: 'contact_no_spc',
      label: 'Contact Number of Single Point of Contact',
      type: 'number',
      required: true,
    },
    {
      name: 'institution_type',
      label: 'Type of Institution',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'school', name: 'School' },
        { id: 'college', name: 'College' },
      ],
    },
    {
      name: 'area_expertise',
      label: 'Area of Expertise of the organization',
      type: 'text',
      required: true,
    },
    {
      name: 'auth_letter',
      required: true,
      id: 'attachment',
      selectedFiles: [],
      label: 'Authorization Letter from Head of the Institution ',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },
  ],
  4: [
    {
      name: 'gender',
      label: 'Gender',
      type: 'radio',
      required: true,
      data: [
        { id: 1, value: 'Male', radioLabel: 'Male' },
        { id: 2, value: 'Female', radioLabel: 'Female' },
        { id: 3, value: 'Other', radioLabel: 'Other' },
      ],
    },
    { name: 'dob', label: 'Date of Birth', type: 'date', required: true },
    { name: 'address', label: 'Address', type: 'textarea', required: true },
    {
      name: 'district',
      id: 'district_id',
      label: 'District',
      type: 'custom_select',
      placeholder: 'Select District',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      data: [],
    },
    {
      name: 'highest_educational_qualification',
      label: 'Highest Qualification',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'Less than 10th', name: 'Less than 10th' },
        { id: '10th', name: '10th' },
        { id: '12th', name: '12th' },
        { id: 'Diploma', name: 'Diploma' },
        { id: 'UG', name: 'UG' },
        { id: 'PG', name: 'PG' },
        { id: 'PhD', name: 'PhD' },
        { id: 'Post Doc', name: 'Post Doc' },
      ],
    },
    {
      name: 'company_name',
      label: 'Name of your Company/ Business',
      type: 'text',
      required: true,
    },
    {
      name: 'business_stage',
      label: 'Stage of your business',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'Just started a business', name: 'Just started a business' },
        {
          id: 'Already in business - Wish to expand',
          name: 'Already in business - Wish to expand',
        },
        {
          id: 'Already in business - Wish to diversify',
          name: 'Already in business - Wish to diversify',
        },
      ],
    },
    {
      name: 'business_duration',
      label: 'How long have you been in this business',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: '0 - 3 years', name: '0-3 years' },
        { id: '3 - 5 years', name: '3-5 years' },
        { id: '5 - 10 years', name: '5-10 years' },
      ],
    },
    {
      name: 'product_type',
      label: 'Specify the Product/ Service',
      type: 'text',
      required: true,
    },
    {
      name: 'business_mode',
      label: 'Mode of business',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        {
          id: 'Manufacturing / Production',
          name: 'Manufacturing / Production',
        },
        { id: 'Packing / Repacking', name: 'Packing / Repacking' },
        { id: 'Trading / Marketing', name: 'Trading / Marketing' },
        { id: 'Services', name: 'Services' },
        { id: 'Other', name: 'Other' },
      ],
    },
    {
      name: 'business_mode_other',
      label: 'Specify Mode of Business',
      type: 'text',
      required: true,
      dependsOn: { field: 'business_mode', value: 'Other' },
    },
  ],
  5: [
    {
      name: 'organization_name',
      label: 'Institute/ Organization Name',
      type: 'text',
      required: true,
    },
    { name: 'address', label: 'Address', type: 'textarea', required: true },
    {
      name: 'head_name',
      label: 'Name of Head of the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'head_designation',
      label: 'Designation of Head of the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'head_email',
      label: 'E-mail of Head of the Institution',
      type: 'email',
      required: true,
    },
    {
      name: 'head_contact',
      label: 'Contact Number of Head of the Institution',
      type: 'number',
      required: true,
    },
    {
      name: 'name_single_poc',
      label: 'Name of Single Point of Contact at the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'designation_single_poc',
      label: 'Designation of Single Point of Contact',
      type: 'text',
      required: true,
    },
    {
      name: 'email_single_poc',
      label: 'E-mail of Single Point of Contact',
      type: 'email',
      required: true,
    },
    {
      name: 'name_single_poc',
      label: 'Contact Number of Single Point of Contact',
      type: 'number',
      required: true,
    },
    {
      name: 'institution_type',
      label: 'Type of Institution',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'School', name: 'School' },
        { id: 'Polytechnic', name: 'Polytechnic' },
        { id: 'ITI', name: 'ITI' },
        { id: 'Professional College', name: 'Professional College' },
        { id: 'Management College', name: 'Management College' },
        { id: 'Arts & Science College', name: 'Arts & Science College' },
        { id: 'other', name: 'Other' },
      ],
    },
    {
      name: 'area_of_expertise',
      label: 'Area of Expertise of the organization',
      type: 'text',
      required: true,
    },
    {
      name: 'authorization_letter',
      required: true,
      id: 'attachment',
      selectedFiles: [],
      label: 'Authorization Letter from Head of the Institution ',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },
  ],
  6: [
    {
      name: 'association_name',
      label: 'Name of the Association/industry body',
      type: 'text',
      required: true,
    },
    { name: 'address', label: 'Address', type: 'textarea', required: true },
    {
      name: 'head_name',
      label: 'Name of Head of the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'head_designation',
      label: 'Designation of Head of the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'head_email',
      label: 'E-Mail of Head of the Institution',
      type: 'email',
      required: true,
    },
    {
      name: 'head_contact',
      label: 'Contact Number of Head of the Institution',
      type: 'number',
      required: true,
    },

    {
      name: 'spoc_name',
      label: 'Name of Single Point of Contact at the Institution',
      type: 'text',
      required: true,
    },
    {
      name: 'spoc_designation',
      label: 'Designation of Single Point of Contact',
      type: 'text',
      required: true,
    },
    {
      name: 'spoc_email',
      label: 'E-Mail of Single Point of Contact',
      type: 'email',
      required: true,
    },
    {
      name: 'spoc_contact',
      label: 'Contact Number of Single Point of Contact',
      type: 'number',
      required: true,
    },

    {
      name: 'institution_type_industry',
      label: 'Institution Type',
      type: 'text',
      required: true,
    },
    {
      name: 'area_of_expertise',
      label: 'Area of Expertise of the organization',
      type: 'text',
      required: true,
    },
    {
      name: 'authorization_letter',
      label: 'Authorization Letter from Head of the Institution',
      type: 'file_upload',
      required: true,
      supportedExtensions: ['pdf', 'docx', 'jpg'],
      supportedFiles: '.pdf, .docx, .jpg format files are allowed',
      fileUploadLabel: 'Choose File',
    },
  ],
  7: [
    {
      name: 'gender',
      label: 'Gender',
      type: 'radio',
      required: true,
      data: [
        { id: 1, value: 'male', radioLabel: 'Male' },
        { id: 2, value: 'female', radioLabel: 'Female' },
        { id: 3, value: 'other', radioLabel: 'Other' },
      ],
    },
    {
      name: 'highest_educational_qualification',
      label: 'Highest Qualification',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'High School', name: 'High School' },
        { id: 'Intermediate', name: 'Intermediate' },
        { id: 'Diploma', name: 'Diploma' },
        { id: 'Bachelors Degree', name: 'Bachelors Degree' },
        { id: 'Masters Degree', name: 'Masters Degree' },
        { id: 'PhD', name: 'PhD' },
        { id: 'Other', name: 'Other' },
      ],
    },
    {
      name: 'profile_url',
      label: 'Website/Online Profile of the Applicant (e.g., LinkedIn)',
      type: 'text',
      required: true,
      placeholder: 'https://linkedin.com/in/your-profile',
    },
    {
      name: 'professional_expertise',
      label: 'Area of Professional Expertise',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'finance', name: 'Finance' },
        { id: 'marketing', name: 'Marketing' },
        { id: 'technology', name: 'Technology' },
        { id: 'operations', name: 'Operations' },
        { id: 'strategy', name: 'Strategy' },
        { id: 'hr', name: 'Human Resources' },
        { id: 'legal', name: 'Legal' },
        { id: 'other', name: 'Other' },
      ],
    },

    {
      name: 'qualification_proof',
      required: false,
      id: 'attachment',
      selectedFiles: [],
      label:
        'Credentials to prove Qualification (.pdf, .docx, .jpg format files are allowed)',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },

    {
      name: 'experience_proof',
      required: false,
      id: 'attachment',
      selectedFiles: [],
      label:
        'Credentials to prove Experience (.pdf, .docx, .jpg format files are allowed)',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },

    {
      name: 'other_empanelment_details',
      required: false,
      id: 'attachment',
      selectedFiles: [],
      label:
        'Details of other Empanelment at other organizations (.pdf, .docx, .jpg format files are allowed)',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },
  ],
  8: [
    {
      name: 'is_member',
      label: 'Do you have a Registered ED Club',
      type: 'radio',
      required: true,
      data: [
        { id: 1, value: 'yes', radioLabel: 'YES' },
        { id: 2, value: 'no', radioLabel: 'NO' },
      ],
    },
    {
      name: 'supporting_document',
      required: false,
      id: 'attachment',
      selectedFiles: [],
      label:
        'Upload Supporting Document (eg. Registration Details, .pdf, .docx, .jpg format files are allowed)',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },
    {
      name: 'institution_type',
      label: 'Type of Institution',
      type: 'custom_select',
      required: true,
      desc: 'name',
      descId: 'id',
      data: [
        { id: 'School', name: 'School' },
        { id: 'College', name: 'College' },
      ],
    },
    {
      name: 'district',
      id: 'district_id',
      label: 'District',
      type: 'custom_select',
      placeholder: 'Select District',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      data: [],
    },
    {
      name: 'address_institution',
      label: 'Address of the School/ College/ Institution',
      type: 'textarea',
      required: true,
    },
    {
      name: 'account_no',
      label: 'Bank Account Number',
      type: 'number',
      required: true,
    },
    {
      name: 'account_name',
      label: 'Account Holder Name',
      type: 'text',
      required: true,
    },
    {
      name: 'bank_name',
      label: 'Name of the Bank',
      type: 'text',
      required: true,
    },
    { name: 'ifsc', label: 'IFSC Code', type: 'text', required: true },
    {
      name: 'approval_letter',
      required: false,
      id: 'attachment',
      selectedFiles: [],
      label:
        'Approval Letter (Authorization letter from head of the institution, .pdf, .docx, .jpg format files are allowed)',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },

    {
      name: 'gst_details',
      required: false,
      id: 'attachment',
      selectedFiles: [],
      label:
        'GST Details (Authorization letter from head: .pdf, .docx, .jpg format files are allowed)',
      descId: 'id',
      supportedExtensions: ACCEPTED_FILE_TYPES,
      supportedFiles: ACCEPTED_FILE_TYPES,
      acceptedFiles: 'upload .jpeg, .jpg, .png, .pdf files',
      type: 'file_upload',
      value: null,
      needConfirmation: true,
      disabled: false,
    },
  ],
  9: [
    { name: 'full_name', label: 'Student Name', type: 'text', required: true },
    {
      name: 'institution_name',
      label: 'Institution/College',
      type: 'text',
      required: true,
    },
    { name: 'course', label: 'Course', type: 'text', required: true },
    {
      name: 'year_of_study',
      label: 'Year of Study',
      type: 'number',
      required: true,
    },
    { name: 'contact_email', label: 'Email', type: 'text', required: true },
  ],
  10: [
    { name: 'full_name', label: 'Full Name', type: 'text', required: true },
    { name: 'organization_name', label: 'Organization (if any)', type: 'text' },
    {
      name: 'purpose_of_visit',
      label: 'Purpose of Visit',
      type: 'textarea',
      required: true,
    },
    { name: 'contact_email', label: 'Email', type: 'text', required: true },
    { name: 'contact_phone', label: 'Phone', type: 'number' },
  ],
}

export default function ProfileCreate({
  isEditing,
  setIsEditing,
}: ProfileCreateProps) {
  const methods = useForm<AdminSchema>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { data: userResp, refetch: refetchUser } = useFetchUserDetails()

  const onSuccess = React.useCallback(async () => {
    await refetchUser()
    methods.reset()
    setIsEditing(false)
  }, [methods, setIsEditing, refetchUser])
  const { data } = useDistrictList()

  const { mutate, isLoading: isCreating } = useUpdateProfile(onSuccess)

  async function buildPayload(details: any, roleIdNum: number | null) {
    if (roleIdNum === 2) {
      const attended = details?.attended_kied_programme
      const isAttended =
        attended === 'yes' || attended === 1 || attended === '1' ? '1' : '0'
      const religions = [
        { id: 'hindu', name: 'Hindu' },
        { id: 'muslim', name: 'Muslim' },
        { id: 'christian', name: 'Christian' },
        { id: 'other', name: 'Other' },
      ]
      let religionVal = ''
      if (details?.religion === 'other') {
        religionVal = details?.religion_other ?? ''
      } else {
        const found = religions.find((r) => r.id === details?.religion)
        religionVal = found ? found.name : (details?.religion ?? '')
      }
      const payload: any = {
        name: details?.name ?? '',
        mobile: details?.mobile ?? '',
        whatsapp_no: details?.whatsapp_no ?? '',
        dob: details?.dob ?? '',
        gender: details?.gender ?? '',
        religion: religionVal,
        caste: details?.caste ?? '',
        qualification:
          details?.highest_qualification ?? details?.qualification ?? '',
        address: details?.address ?? '',
        district: details?.district ?? '',
        business_idea: details?.business_idea ?? '',
        investment_capability: details?.investment_capability ?? '',
        how_you_know_kied:
          details?.how_you_know_kied ?? details?.how_know_kied ?? '',
        is_attended_kied_program: isAttended,
      }
      if (details?.program_details_attended)
        payload.program_details_attended = details.program_details_attended
      return payload
    }
    if (roleIdNum === 3) {
      const fd = new FormData()
      fd.append('role', '3')
      // Common fields
      fd.append('name', details?.name ?? '')
      fd.append('mobile', details?.mobile ?? '')
      fd.append('whatsapp_no', details?.whatsapp_no ?? '')
      fd.append('school_name', details?.school_name ?? '')
      fd.append('address', details?.address ?? '')
      fd.append('head_name', details?.head_name ?? '')
      fd.append('head_email', details?.head_email ?? '')
      fd.append('head_designation', details?.head_designation ?? '')
      fd.append(
        'head_contact_no',
        details?.head_contact_no ?? details?.head_contact ?? ''
      )
      fd.append('name_spc', details?.spoc_name ?? details?.name_spc ?? '')
      fd.append(
        'designation_spc',
        details?.spoc_designation ?? details?.designation_spc ?? ''
      )
      fd.append('email_spc', details?.spoc_email ?? details?.email_spc ?? '')
      fd.append(
        'contact_no_spc',
        details?.spoc_contact ?? details?.contact_no_spc ?? ''
      )
      let instType = details?.institution_type ?? ''
      if (!instType && details?.institution_type?.name) {
        instType = String(details?.institution_type?.name).toLowerCase()
      }
      fd.append('institution_type', instType)
      fd.append(
        'area_expertise',
        details?.area_of_expertise ?? details?.area_expertise ?? ''
      )
      if (details?.auth_letter) {
        const val = details.auth_letter

        if (val instanceof File) {
          fd.append('auth_letter', val, val.name)
        } else if (Array.isArray(val) && val.length > 0) {
          val.forEach((item) => {
            if (item instanceof File) {
              fd.append('auth_letter', item, item.name)
            } else if (item?.file instanceof File) {
              fd.append('auth_letter', item.file, item.file.name)
            }
          })
        } else if (val?.file instanceof File) {
          fd.append('auth_letter', val.file, val.file.name)
        }
      }
      return fd
    }
    if (roleIdNum === 4) {
      const getNameOrValue = (v: any) =>
        v && typeof v === 'object' ? (v.name ?? v.value ?? '') : (v ?? '')
      const payload: any = {
        role: '4',
        name: details?.name ?? '',
        mobile: details?.mobile ?? '',
        whatsapp_no: details?.whatsapp_no ?? '',
        dob: details?.dob ?? '',
        gender: details?.gender ?? '',
        address: details?.address ?? '',
        district: getNameOrValue(details?.district),
        highest_educational_qualification: getNameOrValue(
          details?.highest_educational_qualification
        ),
        company_name: details?.company_name ?? '',
        stage_of_business: getNameOrValue(details?.business_stage),
        product_type: getNameOrValue(details?.product_type),
        how_long_business: getNameOrValue(details?.business_duration),
        mode_of_business: getNameOrValue(details?.business_mode),
      }
      if (
        (details?.business_mode === 'Other' ||
          details?.business_mode?.value === 'Other') &&
        details?.business_mode_other
      ) {
        payload.mode_of_business = details.business_mode_other
      }
      payload.do_you_own_business = payload.company_name ? '1' : '0'
      if (details?.others) payload.others = details.others
      return payload
    }
    if (roleIdNum === 5) {
      // Research/Institution (multipart with file) mapped to backend keys
      const fd = new FormData()
      fd.append('role', '5')
      // Common fields
      fd.append('name', details?.name ?? '')
      fd.append('mobile', details?.mobile ?? '')
      fd.append('whatsapp_no', details?.whatsapp_no ?? '')
      fd.append('organization_name', details?.organization_name ?? '')
      fd.append('address', details?.address ?? '')
      fd.append('head_name', details?.head_name ?? '')
      fd.append('head_designation', details?.head_designation ?? '')
      fd.append('head_email', details?.head_email ?? '')
      // Single Point of Contact mappings
      fd.append('name_single_poc', details?.name_single_poc ?? '')
      fd.append('designation_single_poc', details?.designation_single_poc ?? '')
      fd.append('email_single_poc', details?.email_single_poc ?? '')
      fd.append('contact_single_poc', details?.contact_single_poc ?? '')
      // Other fields
      fd.append('institution_type', details?.institution_type ?? '')
      if (details?.details) fd.append('details', details.details)
      fd.append('area_of_expertise', details?.area_of_expertise ?? '')

      if (details?.authorization_letter) {
        const val = details.authorization_letter

        if (val instanceof File) {
          fd.append('authorization_letter', val, val.name)
        } else if (Array.isArray(val) && val.length > 0) {
          val.forEach((item) => {
            if (item instanceof File) {
              fd.append('authorization_letter', item, item.name)
            } else if (item?.file instanceof File) {
              fd.append('authorization_letter', item.file, item.file.name)
            }
          })
        } else if (val?.file instanceof File) {
          fd.append('authorization_letter', val.file, val.file.name)
        }
      }
      return fd
    }
    if (roleIdNum === 6) {
      // Association/Industry body (multipart)
      const fd = new FormData()
      fd.append('role', '6')
      // Common fields
      fd.append('name', details?.name ?? '')
      fd.append('mobile', details?.mobile ?? '')
      fd.append('whatsapp_no', details?.whatsapp_no ?? '')
      // Organization
      fd.append(
        'organization_name',
        details?.association_name ?? details?.organization_name ?? ''
      )
      fd.append('address', details?.address ?? '')
      // Head details
      fd.append('head_name', details?.head_name ?? '')
      fd.append('head_designation', details?.head_designation ?? '')
      fd.append('head_email', details?.head_email ?? '')
      // Send both keys to satisfy varying backend expectations
      const headContactVal =
        details?.head_contact ?? details?.head_contact_no ?? ''
      fd.append('head_contact', headContactVal)
      fd.append('head_contact_no', headContactVal)
      // Single Point of Contact
      fd.append('name_single_poc', details?.spoc_name ?? '')
      fd.append('designation_single_poc', details?.spoc_designation ?? '')
      fd.append('email_single_poc', details?.spoc_email ?? '')
      fd.append('contact_single_poc', details?.spoc_contact ?? '')
      // Other fields
      // Backend expects `institution_type`; use new string field first, fallback to old
      fd.append(
        'institution_type',
        details?.institution_type_industry ??
          details?.institution_type_industry ??
          ''
      )
      fd.append('area_of_expertise', details?.area_of_expertise ?? '')

      if (details?.authorization_letter) {
        const val = details.authorization_letter

        if (val instanceof File) {
          fd.append('authorization_letter', val, val.name)
        } else if (Array.isArray(val) && val.length > 0) {
          val.forEach((item) => {
            if (item instanceof File) {
              fd.append('authorization_letter', item, item.name)
            } else if (item?.file instanceof File) {
              fd.append('authorization_letter', item.file, item.file.name)
            }
          })
        } else if (val?.file instanceof File) {
          fd.append('authorization_letter', val.file, val.file.name)
        }
      }
      return fd
    }
    if (roleIdNum === 8) {
      // ED Club (multipart because of files)
      const fd = new FormData()
      fd.append('role', '8')
      // Common fields
      fd.append('name', details?.name ?? '')
      fd.append('mobile', details?.mobile ?? '')
      fd.append('whatsapp_no', details?.whatsapp_no ?? '')
      // Map yes/no to 1/0 expected by backend
      const isMember =
        details?.is_member === 'yes' ||
        details?.is_member === 1 ||
        details?.is_member === '1'
          ? '1'
          : '0'
      fd.append('is_member', isMember)
      // Institution type: use text field
      let instType = details?.institution_type ?? ''
      if (!instType && details?.institution_type?.name) {
        instType = String(details?.institution_type?.name).toLowerCase()
      }
      fd.append('institution_type', instType)

      const mapDistrict = (fieldName: string, value: any) => {
        const districtVal = value ?? ''
        const distField = hydratedRoleForm.find((f) => f.name === fieldName)
        const distOptions = (distField?.data ?? []) as {
          id: string
          name: string
        }[]
        if (districtVal && distOptions?.length > 0) {
          const found = distOptions.find(
            (o) =>
              String(o.id).toLowerCase() ===
                String(districtVal).toLowerCase() ||
              String(o.name).toLowerCase() === String(districtVal).toLowerCase()
          )
          if (found) return found.name
        }
        return districtVal
      }

      fd.append(
        'district_institution',
        mapDistrict('district', details?.district)
      )

      fd.append('address_institution', details?.address_institution ?? '')
      fd.append('account_no', details?.account_no ?? '')
      fd.append('account_name', details?.account_name ?? '')
      fd.append('bank_name', details?.bank_name ?? '')
      fd.append('ifsc', details?.ifsc ?? '')

      if (details?.approval_letter) {
        const val = details.approval_letter

        if (val instanceof File) {
          fd.append('approval_letter', val, val.name)
        } else if (Array.isArray(val) && val.length > 0) {
          val.forEach((item) => {
            if (item instanceof File) {
              fd.append('approval_letter', item, item.name)
            } else if (item?.file instanceof File) {
              fd.append('approval_letter', item.file, item.file.name)
            }
          })
        } else if (val?.file instanceof File) {
          fd.append('approval_letter', val.file, val.file.name)
        }
      }

      if (details?.gst_details) {
        const val = details.gst_details

        if (val instanceof File) {
          fd.append('gst_details', val, val.name)
        } else if (Array.isArray(val) && val.length > 0) {
          val.forEach((item) => {
            if (item instanceof File) {
              fd.append('gst_details', item, item.name)
            } else if (item?.file instanceof File) {
              fd.append('gst_details', item.file, item.file.name)
            }
          })
        } else if (val?.file instanceof File) {
          fd.append('gst_details', val.file, val.file.name)
        }
      }

      if (details?.supporting_document) {
        const val = details.supporting_document

        if (val instanceof File) {
          fd.append('supporting_document', val, val.name)
        } else if (Array.isArray(val) && val.length > 0) {
          val.forEach((item) => {
            if (item instanceof File) {
              fd.append('supporting_document', item, item.name)
            } else if (item?.file instanceof File) {
              fd.append('supporting_document', item.file, item.file.name)
            }
          })
        } else if (val?.file instanceof File) {
          fd.append('supporting_document', val.file, val.file.name)
        }
      }

      return fd
    }
    if (roleIdNum === 7) {
      const fd = new FormData()
      fd.append('role', '7')
      // Common fields
      fd.append('name', details?.name ?? '')
      fd.append('mobile', details?.mobile ?? '')
      fd.append('whatsapp_no', details?.whatsapp_no ?? '')
      const getOptionName = (fieldName: string, val: any): string => {
        if (!val) return ''
        if (typeof val === 'string' && !val.trim().includes(':')) {
          const field = hydratedRoleForm.find((f) => f.name === fieldName)
          const options = (field?.data ?? []) as { id: string; name: string }[]
          const found = options.find(
            (o) =>
              String(o.id).toLowerCase() === String(val).toLowerCase() ||
              String(o.name).toLowerCase() === String(val).toLowerCase()
          )
          return found ? found.name : val
        }
        if (typeof val === 'object' && val !== null) {
          return (val.name ?? val.value ?? '').toString()
        }
        return String(val)
      }
      if (details?.gender) {
        const g = String(details.gender).toLowerCase()
        const gv = /^m(ale)?$/.test(g)
          ? 'Male'
          : /^f(emale)?$/.test(g)
            ? 'Female'
            : 'Other'
        fd.append('gender', gv)
      }
      if (details?.highest_educational_qualification) {
        const heqName = getOptionName(
          'highest_educational_qualification',
          details.highest_educational_qualification
        )
        fd.append('highest_educational_qualification', heqName)
      }
      if (details?.profile_url) fd.append('online_profile', details.profile_url)
      if (details?.professional_expertise) {
        const expName = getOptionName(
          'professional_expertise',
          details.professional_expertise
        )
        fd.append('area_expertise', expName)
      }
      // Only append files if the user selected a new one
      if (details?.qualification_proof instanceof File) {
        fd.append(
          'qualification_proof',
          details.qualification_proof,
          details.qualification_proof.name
        )
      } else if (
        Array.isArray(details?.qualification_proof) &&
        details.qualification_proof[0] instanceof File
      ) {
        fd.append(
          'qualification_proof',
          details.qualification_proof[0],
          details.qualification_proof[0].name
        )
      } else if (
        details?.qualification_proof &&
        typeof details.qualification_proof === 'object' &&
        'length' in details.qualification_proof &&
        (details.qualification_proof as any).length > 0 &&
        (details.qualification_proof as any)[0] instanceof File
      ) {
        const f = (details.qualification_proof as any)[0] as File
        fd.append('qualification_proof', f, f.name)
      }

      if (details?.experience_proof instanceof File) {
        fd.append(
          'experience_proof',
          details.experience_proof,
          details.experience_proof.name
        )
      } else if (
        Array.isArray(details?.experience_proof) &&
        details.experience_proof[0] instanceof File
      ) {
        fd.append(
          'experience_proof',
          details.experience_proof[0],
          details.experience_proof[0].name
        )
      } else if (
        details?.experience_proof &&
        typeof details.experience_proof === 'object' &&
        'length' in details.experience_proof &&
        (details.experience_proof as any).length > 0 &&
        (details.experience_proof as any)[0] instanceof File
      ) {
        const f = (details.experience_proof as any)[0] as File
        fd.append('experience_proof', f, f.name)
      }

      if (details?.other_empanelment_details instanceof File) {
        fd.append(
          'other_empanelment',
          details.other_empanelment_details,
          details.other_empanelment_details.name
        )
      } else if (
        Array.isArray(details?.other_empanelment_details) &&
        details.other_empanelment_details[0] instanceof File
      ) {
        fd.append(
          'other_empanelment',
          details.other_empanelment_details[0],
          details.other_empanelment_details[0].name
        )
      } else if (
        details?.other_empanelment_details &&
        typeof details.other_empanelment_details === 'object' &&
        'length' in details.other_empanelment_details &&
        (details.other_empanelment_details as any).length > 0 &&
        (details.other_empanelment_details as any)[0] instanceof File
      ) {
        const f = (details.other_empanelment_details as any)[0] as File
        fd.append('other_empanelment', f, f.name)
      }
      return fd
    }
    return { ...details }
  }

  const { handleSubmit } = methods

  const onSubmit = async (details: any) => {
    // Role 5 requires an authorization letter; show inline error if missing
    if (roleId === 5 || roleId === 6) {
      const val = details?.authorization_letter
      const hasNewFile =
        val instanceof File ||
        (Array.isArray(val) && val.length > 0) ||
        (val && typeof val === 'object' && (val as any).file instanceof File) ||
        (val as any)?.target?.files?.[0] instanceof File

      const existingUrl = Array.isArray(userResp?.user?.profile)
        ? userResp?.user?.profile?.[0]?.authorization_letter
        : undefined

      if (!hasNewFile && !existingUrl) {
        methods.setError('authorization_letter' as any, {
          type: 'required',
          message: 'Authorization letter is required',
        })
        return
      }
    }

    const payload = await buildPayload(details, roleId)
    mutate(payload)
  }

  const { roleData } = useAuthStore()

  const roleId: number | null = roleData?.id ? Number(roleData.id) : null

  const roleName = roleId ? ROLE_DISPLAY_NAME[roleId] : 'Profile'
  const roleForm: FormField[] = roleId ? (formsByRole[roleId] ?? []) : []
  // Hydrate dynamic options (district) for role 4
  const hydratedRoleForm: FormField[] = React.useMemo(() => {
    if (!roleId) return []
    const base = (formsByRole[roleId] ?? []).map((f) => ({ ...f }))
    // Hydrate district options for any role that has a custom_select district field
    const districtIdx = base.findIndex(
      (f) => f.name === 'district' && f.type === 'custom_select'
    )
    if (districtIdx !== -1) {
      if (data?.data) {
        ;(base[districtIdx] as any).data = data.data.map((item: any) => ({
          id: item.id,
          name: item?.district_institution ?? item?.district_name ?? item?.name,
        }))
      }
      ;(base[districtIdx] as any).disabled = !isEditing
    }

    // Disable all file upload fields when not in edit mode
    base.forEach((field) => {
      if (field?.type === 'file_upload') {
        ;(field as any).disabled = !isEditing
      }
    })

    // Role 7 enhancements: make files optional if server already has URLs and inject missing expertise option
    if (roleId === 7) {
      const prof =
        userResp?.user?.profile && Array.isArray(userResp.user.profile)
          ? userResp.user.profile[0]
          : undefined
      if (prof) {
        const qIdx = base.findIndex((f) => f.name === 'qualification_proof')
        if (qIdx !== -1 && prof.qualification_proof) {
          ;(base[qIdx] as any).required = false
          try {
            const u = new URL(prof.qualification_proof)
            const fname =
              u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
              'qualification_proof'
            ;(base[qIdx] as any).selectedFiles = {
              name: fname,
              link: prof.qualification_proof,
            }
          } catch {
            ;(base[qIdx] as any).selectedFiles = {
              name: 'qualification_proof',
              link: prof.qualification_proof,
            }
          }
        }
        const eIdx = base.findIndex((f) => f.name === 'experience_proof')
        if (eIdx !== -1 && prof.experience_proof) {
          ;(base[eIdx] as any).required = false
          try {
            const u = new URL(prof.experience_proof)
            const fname =
              u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
              'experience_proof'
            ;(base[eIdx] as any).selectedFiles = {
              name: fname,
              link: prof.experience_proof,
            }
          } catch {
            ;(base[eIdx] as any).selectedFiles = {
              name: 'experience_proof',
              link: prof.experience_proof,
            }
          }
        }
        const oIdx = base.findIndex(
          (f) => f.name === 'other_empanelment_details'
        )
        if (oIdx !== -1 && prof.other_empanelment) {
          ;(base[oIdx] as any).required = false
          try {
            const u = new URL(prof.other_empanelment)
            const fname =
              u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
              'other_empanelment'
            ;(base[oIdx] as any).selectedFiles = {
              name: fname,
              link: prof.other_empanelment,
            }
          } catch {
            ;(base[oIdx] as any).selectedFiles = {
              name: 'other_empanelment',
              link: prof.other_empanelment,
            }
          }
        }
        // Inject area_expertise option if not present so prefilled value is selectable/editable
        const pIdx = base.findIndex((f) => f.name === 'professional_expertise')
        if (pIdx !== -1 && prof.area_expertise) {
          const opts = ((base[pIdx] as any).data ?? []) as {
            id: string
            name: string
          }[]
          const exists = opts.some(
            (o) =>
              String(o.name).toLowerCase() ===
                String(prof.area_expertise).toLowerCase() ||
              String(o.id).toLowerCase() ===
                String(prof.area_expertise).toLowerCase()
          )
          if (!exists) {
            ;(base[pIdx] as any).data = [
              ...opts,
              {
                id: String(prof.area_expertise),
                name: String(prof.area_expertise),
              },
            ]
          }
        }
      }
    }

    // Role 8 enhancements: if approval_letter/gst_details URLs exist, do not require re-upload
    if (roleId === 8) {
      const club: any =
        userResp?.user && Array.isArray((userResp.user as any).edclub)
          ? (userResp.user as any).edclub[0]
          : undefined
      if (club) {
        const aIdx = base.findIndex((f) => f.name === 'approval_letter')
        if (aIdx !== -1 && club.approval_letter) {
          ;(base[aIdx] as any).required = false
          // Show filename in view mode
          try {
            const u = new URL(club.approval_letter)
            const fname =
              u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
              'approval_letter'
            ;(base[aIdx] as any).selectedFiles = {
              name: fname,
              link: club.approval_letter,
            }
          } catch {
            ;(base[aIdx] as any).selectedFiles = {
              name: 'approval_letter',
              link: club.approval_letter,
            }
          }
        }
        const gIdx = base.findIndex((f) => f.name === 'gst_details')
        if (gIdx !== -1 && club.gst_details) {
          ;(base[gIdx] as any).required = false
          try {
            const u = new URL(club.gst_details)
            const fname =
              u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
              'gst_details'
            ;(base[gIdx] as any).selectedFiles = {
              name: fname,
              link: club.gst_details,
            }
          } catch {
            ;(base[gIdx] as any).selectedFiles = {
              name: 'gst_details',
              link: club.gst_details,
            }
          }
        }
        const sIdx = base.findIndex((f) => f.name === 'supporting_document')
        if (sIdx !== -1 && club.supporting_document) {
          ;(base[sIdx] as any).required = false
          try {
            const u = new URL(club.supporting_document)
            const fname =
              u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
              'supporting_document'
            ;(base[sIdx] as any).selectedFiles = {
              name: fname,
              link: club.supporting_document,
            }
          } catch {
            ;(base[sIdx] as any).selectedFiles = {
              name: 'supporting_document',
              link: club.supporting_document,
            }
          }
        }
      }
    }

    // Role 3: prefill auth_letter if present in profile
    if (roleId === 3) {
      const prof =
        userResp?.user?.profile && Array.isArray(userResp.user.profile)
          ? userResp.user.profile[0]
          : undefined
      const idx = base.findIndex((f) => f.name === 'auth_letter')
      if (idx !== -1 && prof?.auth_letter) {
        ;(base[idx] as any).required = false
        try {
          const u = new URL(prof.auth_letter)
          const fname =
            u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
            'auth_letter'
          ;(base[idx] as any).selectedFiles = {
            name: fname,
            link: prof.auth_letter,
          }
        } catch {
          ;(base[idx] as any).selectedFiles = {
            name: 'auth_letter',
            link: prof.auth_letter,
          }
        }
      }
    }

    // Role 5: prefill authorization_letter if present
    if (roleId === 5) {
      const prof =
        userResp?.user?.profile && Array.isArray(userResp.user.profile)
          ? userResp.user.profile[0]
          : undefined
      const idx = base.findIndex((f) => f.name === 'authorization_letter')
      if (idx !== -1 && prof?.authorization_letter) {
        ;(base[idx] as any).required = false
        try {
          const u = new URL(prof.authorization_letter)
          const fname =
            u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
            'authorization_letter'
          ;(base[idx] as any).selectedFiles = {
            name: fname,
            link: prof.authorization_letter,
          }
        } catch {
          ;(base[idx] as any).selectedFiles = {
            name: 'authorization_letter',
            link: prof.authorization_letter,
          }
        }
      }
    }

    // Role 6: prefill authorization_letter from association
    if (roleId === 6) {
      const assoc: any =
        userResp?.user && Array.isArray((userResp.user as any).association)
          ? (userResp.user as any).association[0]
          : undefined
      const idx = base.findIndex((f) => f.name === 'authorization_letter')
      if (idx !== -1 && assoc?.authorization_letter) {
        ;(base[idx] as any).required = false
        try {
          const u = new URL(assoc.authorization_letter)
          const fname =
            u.pathname.substring(u.pathname.lastIndexOf('/') + 1) ||
            'authorization_letter'
          ;(base[idx] as any).selectedFiles = {
            name: fname,
            link: assoc.authorization_letter,
          }
        } catch {
          ;(base[idx] as any).selectedFiles = {
            name: 'authorization_letter',
            link: assoc.authorization_letter,
          }
        }
      }
    }

    return base as any
  }, [roleId, data, isEditing, userResp])

  React.useEffect(() => {
    if (!userResp || !roleId) return
    const user = userResp.user || {}

    let initialValues: Record<string, any> = {}
    if (
      (roleId === 2 || roleId === 4) &&
      Array.isArray(user.entrepreneur) &&
      user.entrepreneur[0]
    ) {
      initialValues = user.entrepreneur[0]
    } else if (
      roleId === 6 &&
      Array.isArray((user as any).association) &&
      (user as any).association[0]
    ) {
      initialValues = (user as any).association[0]
    } else if (Array.isArray(user.profile) && user.profile[0]) {
      initialValues = user.profile[0]
    }

    const allowedKeys = new Set(hydratedRoleForm.map((f) => f.name))
    const filtered: Record<string, any> = {}
    Object.keys(initialValues || {}).forEach((k) => {
      if (allowedKeys.has(k)) filtered[k] = (initialValues as any)[k]
    })

    // Role 6: map backend keys -> form field names BEFORE reset
    if (roleId === 6) {
      const assoc: any = initialValues || {}
      if (allowedKeys.has('association_name') && assoc.organization_name) {
        filtered['association_name'] = assoc.organization_name
      }
      // Map backend's institution type into our new text field, preferring the editable field first
      if (
        allowedKeys.has('institution_type_industry') &&
        (assoc.institution_type_industry || assoc.institution_type)
      ) {
        filtered['institution_type_industry'] =
          assoc.institution_type_industry ?? assoc.institution_type
      }
      if (allowedKeys.has('spoc_name') && assoc.name_single_poc) {
        filtered['spoc_name'] = assoc.name_single_poc
      }
      if (allowedKeys.has('spoc_designation') && assoc.designation_single_poc) {
        filtered['spoc_designation'] = assoc.designation_single_poc
      }
      if (allowedKeys.has('spoc_email') && assoc.email_single_poc) {
        filtered['spoc_email'] = assoc.email_single_poc
      }
      if (allowedKeys.has('spoc_contact') && assoc.contact_single_poc) {
        filtered['spoc_contact'] = assoc.contact_single_poc
      }
      if (
        allowedKeys.has('head_contact') &&
        (assoc.head_contact || assoc.head_contact_no)
      ) {
        filtered['head_contact'] = assoc.head_contact ?? assoc.head_contact_no
      }
    }

    if (Object.keys(filtered).length > 0) {
      methods.reset(filtered as any)
    }
    if (roleId === 4) {
      const opts = (name: string) =>
        (hydratedRoleForm.find((f) => f.name === name)?.data ?? []) as {
          id: string
          name: string
        }[]
      const mapByName = (name: string, raw?: any) => {
        if (!raw) return ''
        const options = opts('business_mode')
        const found = options.find(
          (o) => String(o.name).toLowerCase() === String(raw).toLowerCase()
        )
        return found ? found.id : raw
      }
      const ent = initialValues
      if (ent?.dob)
        methods.setValue('dob' as any, ent.dob, { shouldDirty: false })
      if (ent?.address)
        methods.setValue('address' as any, ent.address, { shouldDirty: false })
      if (ent?.company_name)
        methods.setValue('company_name' as any, ent.company_name, {
          shouldDirty: false,
        })
      if (ent?.product_type)
        methods.setValue('product_type' as any, ent.product_type, {
          shouldDirty: false,
        })
      if (ent?.gender) {
        const g = String(ent.gender).toLowerCase()
        let gv: 'Male' | 'Female' | 'Other' | undefined
        if (/^m(ale)?$/.test(g)) gv = 'Male'
        else if (/^f(emale)?$/.test(g)) gv = 'Female'
        else gv = 'Other'
        methods.setValue('gender' as any, gv, { shouldDirty: false })
      }
      if (ent?.district)
        methods.setValue(
          'district' as any,
          mapByName('district', ent.district),
          { shouldDirty: false }
        )
      if (ent?.highest_educational_qualification)
        methods.setValue(
          'highest_educational_qualification' as any,
          mapByName(
            'highest_educational_qualification',
            ent.highest_educational_qualification
          ),
          { shouldDirty: false }
        )
      if (ent?.stage_of_business)
        methods.setValue(
          'business_stage' as any,
          mapByName('business_stage', ent.stage_of_business),
          { shouldDirty: false }
        )
      if (ent?.how_long_business)
        methods.setValue(
          'business_duration' as any,
          mapByName('business_duration', ent.how_long_business),
          { shouldDirty: false }
        )
      if (ent?.mode_of_business)
        methods.setValue(
          'business_mode' as any,
          mapByName('business_mode', ent.mode_of_business),
          { shouldDirty: false }
        )
    }
    if (roleId === 7) {
      const ent: any = initialValues
      // Normalize gender to match radio ids
      if (ent?.gender) {
        const g = String(ent.gender).toLowerCase()
        let gv: 'male' | 'female' | 'other' | undefined
        if (/^m(ale)?$/.test(g)) gv = 'male'
        else if (/^f(emale)?$/.test(g)) gv = 'female'
        else gv = 'other'
        methods.setValue('gender' as any, gv, { shouldDirty: false })
      }
      // Map professional_expertise by matching option name or id
      const profField = hydratedRoleForm.find(
        (f) => f.name === 'professional_expertise'
      )
      const profOptions = (profField?.data ?? []) as {
        id: string
        name: string
      }[]
      if (
        ent?.area_expertise &&
        Array.isArray(profOptions) &&
        profOptions.length > 0
      ) {
        const found = profOptions.find(
          (o) =>
            String(o.name).toLowerCase() ===
              String(ent.area_expertise).toLowerCase() ||
            String(o.id).toLowerCase() ===
              String(ent.area_expertise).toLowerCase()
        )
        if (found)
          methods.setValue('professional_expertise' as any, found.id, {
            shouldDirty: false,
          })
        else
          methods.setValue(
            'professional_expertise' as any,
            ent.area_expertise,
            { shouldDirty: false }
          )
      }
      // Map highest_educational_qualification by matching option name -> id
      const heqField = hydratedRoleForm.find(
        (f) => f.name === 'highest_educational_qualification'
      )
      const heqOptions = (heqField?.data ?? []) as {
        id: string
        name: string
      }[]
      if (
        ent?.highest_educational_qualification &&
        Array.isArray(heqOptions) &&
        heqOptions.length > 0
      ) {
        const foundHeq = heqOptions.find(
          (o) =>
            String(o.name).toLowerCase() ===
              String(ent.highest_educational_qualification).toLowerCase() ||
            String(o.id).toLowerCase() ===
              String(ent.highest_educational_qualification).toLowerCase()
        )
        if (foundHeq)
          methods.setValue(
            'highest_educational_qualification' as any,
            foundHeq.id,
            { shouldDirty: false }
          )
        else
          methods.setValue(
            'highest_educational_qualification' as any,
            ent.highest_educational_qualification,
            { shouldDirty: false }
          )
      }
      if (ent?.online_profile)
        methods.setValue('profile_url' as any, ent.online_profile, {
          shouldDirty: false,
        })
    }
    // Role 8: prefill from edclub[0] and map to our form fields
    if (roleId === 8) {
      const club: any =
        (user as any)?.edclub && Array.isArray((user as any).edclub)
          ? (user as any).edclub[0]
          : undefined
      if (club) {
        // yes/no mapping
        const isMember =
          club.is_member === 1 ||
          club.is_member === '1' ||
          club.is_member === true
            ? 'yes'
            : 'no'
        if (allowedKeys.has('is_member'))
          methods.setValue('is_member' as any, isMember, { shouldDirty: false })
        // simple text fields
        if (
          allowedKeys.has('institution_type') &&
          (club.institution_type || club.institution_type)
        ) {
          const instField = hydratedRoleForm.find(
            (f) => f.name === 'institution_type'
          )
          const instOptions = (instField?.data ?? []) as {
            id: string
            name: string
          }[]
          if (Array.isArray(instOptions) && instOptions.length > 0) {
            const foundInst = instOptions.find(
              (o) =>
                String(o.name).toLowerCase() ===
                  String(club.institution_type).toLowerCase() ||
                String(o.id).toLowerCase() ===
                  String(club.institution_type).toLowerCase()
            )
            if (foundInst) {
              methods.setValue('institution_type' as any, foundInst.name, {
                shouldDirty: false,
              })
            } else {
              methods.setValue(
                'institution_type' as any,
                club.institution_type,
                { shouldDirty: false }
              )
            }
          } else {
            methods.setValue('institution_type' as any, club.institution_type, {
              shouldDirty: false,
            })
          }
        }
        if (allowedKeys.has('address') && club.address_institution)
          methods.setValue('address' as any, club.address_institution, {
            shouldDirty: false,
          })
        if (allowedKeys.has('account_no') && club.account_no)
          methods.setValue('account_no' as any, club.account_no, {
            shouldDirty: false,
          })
        if (allowedKeys.has('account_name') && club.account_name)
          methods.setValue('account_name' as any, club.account_name, {
            shouldDirty: false,
          })
        if (allowedKeys.has('bank_name') && club.bank_name)
          methods.setValue('bank_name' as any, club.bank_name, {
            shouldDirty: false,
          })
        if (allowedKeys.has('ifsc') && club.ifsc)
          methods.setValue('ifsc' as any, club.ifsc, { shouldDirty: false })
        const districtField = hydratedRoleForm.find(
          (f) => f.name === 'district'
        )
        const districtOptions = (districtField?.data ?? []) as {
          id: string
          name: string
        }[]
        if (club?.district_institution) {
          if (districtOptions.length > 0) {
            const foundDist = districtOptions.find(
              (o) =>
                String(o.name).toLowerCase() ===
                String(club.district_institution).toLowerCase()
            )
            if (foundDist) {
              methods.setValue('district' as any, foundDist.name, {
                shouldDirty: false,
              })
            } else {
              methods.setValue('district' as any, club.district_institution, {
                shouldDirty: false,
              })
            }
          } else {
            methods.setValue('district' as any, club.district_institution, {
              shouldDirty: false,
            })
          }
        }
      }
    }
    if (user?.name)
      methods.setValue('name' as any, user.name, { shouldDirty: false })
    if (user?.mobile)
      methods.setValue('mobile' as any, user.mobile, { shouldDirty: false })
    if (user?.whatsapp_no)
      methods.setValue('whatsapp_no' as any, user.whatsapp_no, {
        shouldDirty: false,
      })

    const rawCaste: string | undefined =
      (initialValues as any)?.caste || user?.caste
    if (rawCaste) {
      const norm = String(rawCaste).toLowerCase()
      let mapped: 'sc_st' | 'obc' | 'general' | undefined
      if (/(sc\/?st|scst)/.test(norm)) mapped = 'sc_st'
      else if (/obc/.test(norm)) mapped = 'obc'
      else if (/general/.test(norm)) mapped = 'general'
      if (mapped)
        methods.setValue('caste' as any, mapped, { shouldDirty: false })
    }
    const attendedFlag: any =
      (initialValues as any)?.is_attended_kied_program ??
      user?.is_attended_kied_program
    if (attendedFlag !== undefined && attendedFlag !== null) {
      const mapped =
        attendedFlag === 1 ||
        attendedFlag === '1' ||
        attendedFlag === true ||
        attendedFlag === 'yes'
          ? 'yes'
          : 'no'
      methods.setValue('attended_kied_programme' as any, mapped, {
        shouldDirty: false,
      })
    }
  }, [userResp, roleId, methods, roleForm, data])

  console.log(userResp)
  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col lg:flex-row w-full gap-6 lg:px-10 p-4"
      >
        <div className="w-full space-y-6">
          <div>
            <div className="mb-6">
              <h2 className="text-lg font-medium">User Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div className="p-3 border rounded">
                  <p className="text-xs font-medium">Name</p>
                  {isEditing ? (
                    <input
                      type="text"
                      className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primaryOrange"
                      placeholder="Enter name"
                      onKeyDown={(e) => {
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'Tab',
                          'ArrowLeft',
                          'ArrowRight',
                          'Home',
                          'End',
                          ' ',
                        ]
                        const isLetter = /^[a-zA-Z]$/.test(e.key)
                        if (
                          allowedKeys.includes(e.key) ||
                          isLetter ||
                          e.ctrlKey ||
                          e.metaKey
                        )
                          return
                        e.preventDefault()
                      }}
                      onPaste={(e) => {
                        const text = e.clipboardData.getData('text')
                        const cleaned = text
                          .replace(/[^A-Za-z ]+/g, ' ')
                          .replace(/\s+/g, ' ')
                          .trimStart()
                        if (cleaned !== text) {
                          e.preventDefault()
                          const target = e.target as HTMLInputElement
                          const start =
                            target.selectionStart ?? target.value.length
                          const end = target.selectionEnd ?? start
                          const next = (
                            target.value.slice(0, start) +
                            cleaned +
                            target.value.slice(end)
                          )
                            .replace(/[^A-Za-z ]+/g, ' ')
                            .replace(/\s+/g, ' ')
                          target.value = next
                          const evt = new Event('input', { bubbles: true })
                          target.dispatchEvent(evt)
                        }
                      }}
                      onInput={(e) => {
                        const target = e.currentTarget
                        const cleaned = target.value
                          .replace(/[^A-Za-z ]+/g, ' ')
                          .replace(/\s+/g, ' ')
                        if (cleaned !== target.value) target.value = cleaned
                      }}
                      {...methods.register('name' as any, {
                        setValueAs: (v: any) =>
                          String(v ?? '')
                            .replace(/[^A-Za-z ]+/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim(),
                      })}
                      defaultValue={userResp?.user?.name ?? ''}
                    />
                  ) : (
                    <p className="text-sm font-medium">
                      {userResp?.user?.name ?? '-'}
                    </p>
                  )}
                </div>
                <div className="p-3 border rounded">
                  <p className="text-xs font-medium">Phone.No</p>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        onPaste={(e) => {
                          const text = e.clipboardData.getData('text')
                          if (/[^0-9]/.test(text)) {
                            e.preventDefault()
                            const digits = text.replace(/\D/g, '').slice(0, 10)
                            const target = e.target as HTMLInputElement
                            const start =
                              target.selectionStart ?? target.value.length
                            const end = target.selectionEnd ?? start
                            const next = (
                              target.value.slice(0, start) +
                              digits +
                              target.value.slice(end)
                            )
                              .replace(/\D/g, '')
                              .slice(0, 10)
                            target.value = next
                            const evt = new Event('input', { bubbles: true })
                            target.dispatchEvent(evt)
                          }
                        }}
                        onInput={(e) => {
                          const target = e.currentTarget
                          const cleaned = target.value
                            .replace(/\D/g, '')
                            .slice(0, 10)
                          if (target.value !== cleaned) target.value = cleaned
                        }}
                        onKeyDown={(e) => {
                          const allowed = [
                            'Backspace',
                            'Delete',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'Home',
                            'End',
                          ]
                          if (
                            allowed.includes(e.key) ||
                            // Allow Ctrl/Cmd+A/C/V/X/Z/Y
                            e.ctrlKey ||
                            e.metaKey ||
                            /\d/.test(e.key)
                          ) {
                            return
                          }
                          e.preventDefault()
                        }}
                        className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primaryOrange"
                        placeholder="Enter phone number"
                        {...methods.register('mobile' as any, {
                          setValueAs: (v: any) =>
                            String(v ?? '')
                              .replace(/\D/g, '')
                              .slice(0, 10),
                        })}
                      />
                      {methods.formState.errors?.mobile && (
                        <p className="text-red-600 text-xs mt-1">
                          {(methods.formState.errors.mobile as any).message}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm font-medium">
                      {userResp?.user?.mobile ?? '-'}
                    </p>
                  )}
                </div>
                <div className="p-3 border rounded">
                  <p className="text-xs font-medium">WhatsApp Number</p>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        inputMode="numeric"
                        onPaste={(e) => {
                          const text = e.clipboardData.getData('text')
                          if (/[^0-9]/.test(text)) {
                            e.preventDefault()
                            const digits = text.replace(/\D/g, '').slice(0, 10)
                            const target = e.target as HTMLInputElement
                            const start =
                              target.selectionStart ?? target.value.length
                            const end = target.selectionEnd ?? start
                            const next = (
                              target.value.slice(0, start) +
                              digits +
                              target.value.slice(end)
                            )
                              .replace(/\D/g, '')
                              .slice(0, 10)
                            target.value = next
                            const evt = new Event('input', { bubbles: true })
                            target.dispatchEvent(evt)
                          }
                        }}
                        onInput={(e) => {
                          const target = e.currentTarget
                          const cleaned = target.value
                            .replace(/\D/g, '')
                            .slice(0, 10)
                          if (target.value !== cleaned) target.value = cleaned
                        }}
                        maxLength={10}
                        onKeyDown={(e) => {
                          const allowed = [
                            'Backspace',
                            'Delete',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'Home',
                            'End',
                          ]
                          if (
                            allowed.includes(e.key) ||
                            e.ctrlKey ||
                            e.metaKey ||
                            /\d/.test(e.key)
                          ) {
                            return
                          }
                          e.preventDefault()
                        }}
                        className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primaryOrange"
                        placeholder="Enter WhatsApp number"
                        {...methods.register('whatsapp_no' as any, {
                          setValueAs: (v: any) =>
                            String(v ?? '')
                              .replace(/\D/g, '')
                              .slice(0, 10),
                        })}
                      />
                      {methods.formState.errors?.whatsapp_no && (
                        <p className="text-red-600 text-xs mt-1">
                          {
                            (methods.formState.errors.whatsapp_no as any)
                              .message
                          }
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm font-medium">
                      {userResp?.user?.whatsapp_no ?? '-'}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <h2 className="text-xl font-medium">{roleName} Information</h2>
              <p className="text-LightGray text-sm">
                Please Fill Out All Required Fields
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 max-w-2xl  w-full">
              <FormBuilder data={hydratedRoleForm} edit={isEditing} />
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 mt-4 w-full justify-end">
            {isEditing && (
              <Button
                type="submit"
                className="px-10 py-2 text-white rounded disabled:opacity-50 w-full !bg-primaryOrange"
                disabled={isCreating}
                label="Submit"
              />
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
